import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import * as icons from '@creditornot/alchemy-react/icons';
import { TextButton } from '@creditornot/cb-button';
import { Popover } from '@creditornot/cb-popover';
import { SegmentedControl } from '@creditornot/cb-segmented-control';
import { Venue } from 'common/api-types/director';
import { ArrayFilterTag } from 'common/components/cookbook/ArrayFilterTag';
import { CountryItemsFilter } from 'common/components/cookbook/filters/CountryItemsFilter/CountryItemsFilter';
import { ProductFamiliesFilter } from 'common/components/cookbook/filters/ProductFamiliesFilter/ProductFamiliesFilter';
import { useProductFamiliesFilterTagText } from 'common/components/cookbook/filters/ProductFamiliesFilter/useProductFamiliesFilterTagText';
import { PopoverContent } from 'common/components/cookbook/PopoverContent';
import { Stack } from 'common/components/cookbook/Stack';
import { useFormatMessage } from 'common/hooks/useFormatMessage';

import { overrideManagementRouteApi } from '../routeApi';
import { ReasonCodesFilterPopoverContent } from './ReasonCodesFilterPopoverContent';
import { StatusFilterPopoverContent } from './StatusFilterPopoverContent';

const FilterContainer = styled(Stack)`
  padding: 24px;
`;

const FilterPopoverContent = styled.div`
  padding: 12px;
`;

type Props = {
  venues: Venue[];
  isVenuesLoading: boolean;
  venuesError: Error | null;
};

export const OverridesFilter = ({ venues, isVenuesLoading, venuesError }: Props) => {
  const format = useFormatMessage();

  const filterSegments = [
    { label: format('common.status'), value: 'status' },
    { label: format('forecasting.reason'), value: 'reason' },
    { label: format('common.venue'), value: 'venue' },
    { label: format('forecasting.family'), value: 'productFamily' },
  ];

  const [filterType, setFilterType] = useState(filterSegments[0].value);
  const [venueSearch, setVenueSearch] = useState('');

  const filteredVenues = useMemo(() => {
    if (venueSearch.length > 2)
      return venues.filter(
        (venue) =>
          venue.name.toLowerCase().includes(venueSearch.toLowerCase()) ||
          venue.code.toLowerCase().includes(venueSearch.toLowerCase()),
      );
    else return venues;
  }, [venues, venueSearch]);

  const {
    productFamilies = [],
    statuses = [],
    overrideReasons = [],
    venueCodes = [],
  } = overrideManagementRouteApi.useSearch();

  const navigate = overrideManagementRouteApi.useNavigate();

  const productFamiliesFilterTagItems = useProductFamiliesFilterTagText(productFamilies);
  const overrideReasonsFilterTagItems = overrideReasons.map((reason) => {
    return format(`forecasting.reason_code:${reason}`);
  });
  const statusesFilterTagItems = statuses.map((status) => {
    return format(`forecasting.status:${status}`);
  });

  return (
    <FilterContainer direction="row" gap="regularMedium">
      <Popover
        placement="bottom-start"
        content={
          <PopoverContent>
            <SegmentedControl
              value={filterType}
              options={filterSegments}
              onChange={(value) => setFilterType(value)}
            />
            <FilterPopoverContent>
              {filterType === 'status' && <StatusFilterPopoverContent />}
              {filterType === 'reason' && <ReasonCodesFilterPopoverContent />}
              {filterType === 'venue' && (
                <CountryItemsFilter<Venue>
                  title={format('common.venues')}
                  emptyTitle={format('invoices.filter.list_filter_empty', {
                    filterName: format('common.venue').toLocaleLowerCase(),
                  })}
                  items={filteredVenues ?? []}
                  filterItems={venueCodes}
                  isLoading={isVenuesLoading}
                  error={venuesError}
                  searchQuery={venueSearch}
                  onSearchQueryChange={setVenueSearch}
                  onChange={(value) =>
                    void navigate({
                      search: (prev) => ({
                        ...prev,
                        venueCodes: value.length > 0 ? value : undefined,
                        page: undefined,
                      }),
                    })
                  }
                />
              )}
              {filterType === 'productFamily' && (
                <ProductFamiliesFilter
                  productFamilyIds={productFamilies}
                  onProductFamiliesChange={(value) =>
                    void navigate({
                      search: (prev) => ({
                        ...prev,
                        productFamilies: value.length ? value : undefined,
                        page: undefined,
                      }),
                    })
                  }
                />
              )}
            </FilterPopoverContent>
          </PopoverContent>
        }
      >
        {({ refProps }) => (
          <TextButton {...refProps} variant="neutral" icon={icons.plus} iconPosition="end">
            <FormattedMessage id="common.filter" />
          </TextButton>
        )}
      </Popover>
      <ArrayFilterTag
        items={venueCodes}
        label={format('common.venues')}
        onRemoveClick={() =>
          void navigate({
            search: (prev) => ({ ...prev, venueCodes: undefined, page: undefined }),
          })
        }
        popoverContent={
          <CountryItemsFilter<Venue>
            title={format('common.venues')}
            emptyTitle={format('invoices.filter.list_filter_empty', {
              filterName: format('common.venue').toLocaleLowerCase(),
            })}
            items={filteredVenues ?? []}
            filterItems={venueCodes}
            isLoading={isVenuesLoading}
            error={venuesError}
            searchQuery={venueSearch}
            onSearchQueryChange={setVenueSearch}
            onChange={(value) =>
              void navigate({
                search: (prev) => ({
                  ...prev,
                  venueCodes: value.length ? value : undefined,
                  page: undefined,
                }),
              })
            }
          />
        }
      />
      <ArrayFilterTag
        items={productFamiliesFilterTagItems}
        label={format('common.product_families')}
        onRemoveClick={() =>
          void navigate({
            search: (prev) => ({ ...prev, productFamilies: undefined, page: undefined }),
          })
        }
        popoverContent={
          <ProductFamiliesFilter
            productFamilyIds={productFamilies}
            onProductFamiliesChange={(value) =>
              void navigate({
                search: (prev) => ({
                  ...prev,
                  productFamilies: value.length ? value : undefined,
                  page: undefined,
                }),
              })
            }
          />
        }
      />
      <ArrayFilterTag
        items={overrideReasonsFilterTagItems}
        label={format('forecasting.override_reasons_label')}
        onRemoveClick={() =>
          void navigate({
            search: (prev) => ({ ...prev, overrideReasons: undefined, page: undefined }),
          })
        }
        popoverContent={<ReasonCodesFilterPopoverContent />}
      />
      <ArrayFilterTag
        items={statusesFilterTagItems}
        label={format('forecasting.statuses')}
        onRemoveClick={() =>
          void navigate({
            search: (prev) => ({ ...prev, statuses: undefined, page: undefined }),
          })
        }
        popoverContent={<StatusFilterPopoverContent />}
      />
    </FilterContainer>
  );
};
